/**
 * lib/leaveCard.ts
 * ------------------------------------------------------------------
 * HR LEAVE — the yearly leave card, as HR has kept it on paper (Excel)
 * for the teachers: one row per month, leave earned, leave and sick days
 * taken, the running balance, notes. lib/leaveCardPdf prints it.
 *
 * Pure: the caller passes the person's allocation, approved leave and the
 * date. The running balance is the engine's own (lib/hrLeave): the
 * opening balance as at the cohort's anchor, a month's share credited once
 * a full month has passed since it (monthsAccrued — the 30th, for a
 * 30 September balance), annual leave taken off — so the card and every
 * leave screen show the same figure, the current month included. Months before the anchor come from the
 * card's own header (`leaveCard2026`: C/F into the year) where the 2026
 * import stored it; otherwise they show what was taken, with no balance.
 * ------------------------------------------------------------------ */

import { monthsAccrued } from '@/lib/hrLeave';

export interface LeaveCardHeader {
  decBal25: number;
  holDays: number;
  sold: number;
  cf2026: number;
  /** First month on the card (0 = Jan) — later for someone who joined mid-year. */
  firstMonth?: number;
}

export interface LeaveCardRow {
  /** 0 = Jan. */
  month: number;
  label: string;
  /** Days earned that month — blank before the person was on the card. */
  earned: number | null;
  taken: number;
  sick: number;
  maternity: number;
  /** Running annual balance at the end of the month; null for months to come. */
  balance: number | null;
  notes: string;
}

export interface LeaveCard {
  year: number;
  rows: LeaveCardRow[];
  totals: { earned: number; taken: number; sick: number; maternity: number };
  /** Balance at the latest month shown. */
  balance: number | null;
  /** Whether any maternity is on the card (the column prints only then). */
  hasMaternity: boolean;
  header?: LeaveCardHeader;
}

export const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const r2 = (n: number) => Math.round(n * 100) / 100;

export function buildLeaveCard(input: {
  year: number;
  /** Annual days a year (the resolved entitlement). */
  entitlement: number;
  /** Opening annual balance "as at" `anchor` (the allocation's openingBalances.annual). */
  opening: number;
  /** YYYY-MM-DD the opening is as at (30 Sep 2026 for teachers). */
  anchor: string;
  employmentStart?: string;
  header?: LeaveCardHeader;
  leave: { type: string; days: number; startDate: string; status: string; reason?: string }[];
  asOf: Date;
}): LeaveCard {
  const { year, entitlement } = input;
  const perMonth = entitlement / 12;
  const shown = r2(perMonth);
  // The engine's anchor: the cohort's "as at" date, or the start date if later.
  const anchorISO = input.employmentStart && input.employmentStart.slice(0, 10) > input.anchor ? input.employmentStart.slice(0, 10) : input.anchor;
  const anchorDate = new Date(`${anchorISO}T12:00:00`);
  const anchorMonth = anchorDate.getFullYear() < year ? -1 : anchorDate.getMonth();
  const opening = input.opening;
  const start = input.employmentStart ? new Date(input.employmentStart) : null;
  /** Months the engine had credited by the end of month m — or by asOf, for the month we are in. */
  const creditedBy = (m: number) => {
    const monthEnd = new Date(year, m + 1, 0, 12);
    return monthsAccrued(anchorISO, monthEnd < input.asOf ? monthEnd : input.asOf);
  };
  const lastMonth = input.asOf.getFullYear() > year ? 11 : input.asOf.getFullYear() < year ? -1 : input.asOf.getMonth();
  const firstMonth = input.header?.firstMonth ?? (start && start.getFullYear() === year ? start.getMonth() : 0);

  const sum = (type: string, m: number) => r2(input.leave
    .filter((l) => l.status === 'approved' && l.type === type && new Date(l.startDate).getUTCFullYear() === year && new Date(l.startDate).getUTCMonth() === m)
    .reduce((t, l) => t + (Number(l.days) || 0), 0));
  const notesFor = (m: number) => input.leave
    .filter((l) => l.status === 'approved' && new Date(l.startDate).getUTCFullYear() === year && new Date(l.startDate).getUTCMonth() === m && l.reason && l.reason !== '2026 leave card')
    .map((l) => l.reason!.trim()).filter((v, i, a) => a.indexOf(v) === i).join('; ');

  const rows: LeaveCardRow[] = [];
  let preBal: number | null = input.header ? input.header.cf2026 : null;
  let takenSinceAnchor = 0;
  let creditedBefore = 0;
  for (let m = 0; m < 12; m++) {
    const on = m >= firstMonth;
    const taken = sum('annual', m);
    let balance: number | null = null;
    let earned: number | null = on ? shown : null;
    if (m <= anchorMonth) {
      // Up to the engine's anchor: the card's own arithmetic, from its C/F.
      if (preBal !== null && on) preBal = r2(preBal + shown - taken);
      balance = m === anchorMonth ? r2(opening) : preBal;
    } else {
      // After it: exactly what the engine credits — a month once a full month
      // has passed since the anchor — so the current month reads as the screens do.
      const credited = creditedBy(m);
      earned = m <= lastMonth ? r2(shown * (credited - creditedBefore)) : shown;
      creditedBefore = credited;
      takenSinceAnchor += taken;
      balance = r2(opening + perMonth * credited - takenSinceAnchor);
    }
    rows.push({
      month: m, label: MONTH_LABELS[m], earned, taken, sick: sum('sick', m), maternity: sum('maternity', m),
      balance: on && m <= lastMonth ? balance : null, notes: notesFor(m),
    });
  }
  const totals = {
    earned: r2(rows.reduce((t, r) => t + (r.earned ?? 0), 0)),
    taken: r2(rows.reduce((t, r) => t + r.taken, 0)),
    sick: r2(rows.reduce((t, r) => t + r.sick, 0)),
    maternity: r2(rows.reduce((t, r) => t + r.maternity, 0)),
  };
  const latest = [...rows].reverse().find((r) => r.balance !== null);
  return { year, rows, totals, balance: latest?.balance ?? null, hasMaternity: totals.maternity > 0, header: input.header };
}
